import { projectsBillingRecipientSnapshotCapability } from '@crest/module-projects/contracts/billing-recipient';
import { z } from 'zod';
export const financeInvoiceIdSchema = z.string().uuid();
export const financeProjectReferenceIdSchema = z.string().uuid();
export const financeInvoiceCommandIdSchema = z.string().uuid();
export const financeInvoicesCapability = 'finance.invoices.v1';
export const financeDateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}, 'Finance dates must be real ISO calendar dates.');
export const financeMoneyCentsSchema = z.number().int().min(0).max(1_000_000_000_000_000);
export const financeUnitPriceCentsSchema = z.number().int().min(0).max(10_000_000_000);
export const financeQuantityTenThousandthsSchema = z.number().int().min(1).max(100_000_000);
export const financeTaxBasisPointsSchema = z.number().int().min(0).max(10_000);
export const financeInvoiceStatusSchema = z.enum(['draft', 'sent', 'partial', 'paid', 'void']);
const nullableLineText = (maximum) => z.string().trim().min(1).max(maximum).nullable();
const optionalCapabilitySchema = z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/).nullable();
export const financeBillingRecipientSnapshotSchema = z.strictObject({
    contractVersion: z.literal(projectsBillingRecipientSnapshotCapability),
    projectRevision: z.number().int().positive(),
    name: z.string().max(240).nullable(),
    email: z.string().email().max(320).nullable(),
    addressLine1: z.string().max(240).nullable(),
    addressLine2: z.string().max(240).nullable(),
    city: z.string().max(120).nullable(),
    region: z.string().max(120).nullable(),
    postalCode: z.string().max(40).nullable(),
    countryCode: z.string().regex(/^[A-Z]{2}$/).nullable(),
});
const financeInvoiceLineShape = {
    sourceCapability: optionalCapabilitySchema.default(null),
    sourceId: nullableLineText(240).default(null),
    sourceLabel: nullableLineText(240).default(null),
    groupLabel: nullableLineText(120).default(null),
    description: z.string().trim().min(1).max(500),
    quantityTenThousandths: financeQuantityTenThousandthsSchema,
    unitLabel: z.string().trim().min(1).max(20).default('ea'),
    unitPriceCents: financeUnitPriceCentsSchema,
    taxable: z.boolean().default(true),
};
function validateFinanceInvoiceLineSource(line, context) {
    if ((line.sourceCapability === null) !== (line.sourceId === null)) {
        context.addIssue({
            code: 'custom',
            path: ['sourceId'],
            message: 'Finance line source capability and identity must be retained together.',
        });
    }
}
export const financeInvoiceLineInputSchema = z.strictObject(financeInvoiceLineShape)
    .superRefine(validateFinanceInvoiceLineSource);
export const financeInvoiceLineSchema = z.strictObject({
    ...financeInvoiceLineShape,
    id: z.string().uuid(),
    position: z.number().int().min(1).max(100),
    lineTotalCents: financeMoneyCentsSchema,
}).superRefine(validateFinanceInvoiceLineSource);
const financeInvoiceBaseSchema = z.strictObject({
    id: financeInvoiceIdSchema,
    tenantId: z.string().uuid(),
    revision: z.number().int().positive(),
    contentRevision: z.number().int().positive(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    projectReferenceId: financeProjectReferenceIdSchema,
    projectReferenceNumber: z.string().min(1).max(80),
    projectReferenceName: z.string().min(1).max(240),
    billingRecipient: financeBillingRecipientSnapshotSchema,
    invoiceNumber: z.string().min(1).max(40),
    status: financeInvoiceStatusSchema,
    currencyCode: z.literal('USD'),
    invoiceDate: financeDateOnlySchema,
    dueDate: financeDateOnlySchema,
    memo: z.string().max(2_000).nullable(),
    subtotalCents: financeMoneyCentsSchema,
    discountCents: financeMoneyCentsSchema,
    taxBasisPoints: financeTaxBasisPointsSchema,
    taxCents: financeMoneyCentsSchema,
    totalCents: financeMoneyCentsSchema,
    amountPaidCents: financeMoneyCentsSchema,
    amountWrittenOffCents: financeMoneyCentsSchema,
    amountDueCents: financeMoneyCentsSchema,
    issuedAt: z.string().datetime({ offset: true }).nullable(),
    settledAt: z.string().datetime({ offset: true }).nullable(),
    voidedAt: z.string().datetime({ offset: true }).nullable(),
    voidReason: z.string().trim().min(1).max(1_000).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
function validateFinanceInvoiceHeader(invoice, context) {
    if (invoice.dueDate < invoice.invoiceDate) {
        context.addIssue({
            code: 'custom',
            path: ['dueDate'],
            message: 'Finance invoice due date cannot precede its invoice date.',
        });
    }
    if (invoice.discountCents > invoice.subtotalCents) {
        context.addIssue({
            code: 'custom',
            path: ['discountCents'],
            message: 'Finance invoice discount cannot exceed its subtotal.',
        });
    }
    const expectedTotal = invoice.subtotalCents - invoice.discountCents + invoice.taxCents;
    if (invoice.totalCents !== expectedTotal) {
        context.addIssue({
            code: 'custom',
            path: ['totalCents'],
            message: 'Finance invoice total does not reconcile.',
        });
    }
    if (invoice.amountPaidCents + invoice.amountWrittenOffCents > invoice.totalCents) {
        context.addIssue({
            code: 'custom',
            path: ['amountPaidCents'],
            message: 'Finance invoice payments and write-offs cannot exceed its total.',
        });
    }
    const expectedDue = Math.max(0, invoice.totalCents - invoice.amountPaidCents - invoice.amountWrittenOffCents);
    if (invoice.amountDueCents !== expectedDue) {
        context.addIssue({
            code: 'custom',
            path: ['amountDueCents'],
            message: 'Finance invoice amount due does not reconcile.',
        });
    }
    if ((invoice.status === 'void') !==
        (invoice.voidedAt !== null && invoice.voidReason !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['voidedAt'],
            message: 'Finance invoice void state requires retained time and reason evidence.',
        });
    }
    if (invoice.status !== 'void' && (invoice.voidedAt !== null || invoice.voidReason !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['voidReason'],
            message: 'A nonvoid Finance invoice cannot carry void evidence.',
        });
    }
    if (invoice.status === 'sent' && invoice.issuedAt === null) {
        context.addIssue({
            code: 'custom',
            path: ['issuedAt'],
            message: 'An issued Finance invoice requires its issue time.',
        });
    }
    if ((invoice.status === 'paid') !==
        (invoice.amountDueCents === 0 && invoice.settledAt !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['settledAt'],
            message: 'Finance invoice paid state must match exact settlement evidence.',
        });
    }
}
export const financeInvoiceSchema = financeInvoiceBaseSchema.superRefine(validateFinanceInvoiceHeader);
export const financeInvoiceDetailSchema = financeInvoiceBaseSchema.extend({
    lineItems: z.array(financeInvoiceLineSchema).min(1).max(100),
}).superRefine((invoice, context) => {
    validateFinanceInvoiceHeader(invoice, context);
    let lineSubtotal = 0n;
    invoice.lineItems.forEach((line, index) => {
        const expectedLineTotal = ((BigInt(line.quantityTenThousandths) * BigInt(line.unitPriceCents)) + 5000n) / 10000n;
        if (BigInt(line.lineTotalCents) !== expectedLineTotal) {
            context.addIssue({
                code: 'custom',
                path: ['lineItems', index, 'lineTotalCents'],
                message: 'Finance invoice line total does not reconcile.',
            });
        }
        lineSubtotal += expectedLineTotal;
    });
    if (lineSubtotal !== BigInt(invoice.subtotalCents)) {
        context.addIssue({
            code: 'custom',
            path: ['subtotalCents'],
            message: 'Finance invoice subtotal does not match its line items.',
        });
    }
});
export const financeCreateInvoiceDraftRequestSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    projectId: financeProjectReferenceIdSchema,
    invoiceDate: financeDateOnlySchema.optional(),
    dueDate: financeDateOnlySchema.optional(),
    memo: z.string().trim().max(2_000).nullable().default(null),
    discountCents: financeMoneyCentsSchema.default(0),
    taxBasisPoints: financeTaxBasisPointsSchema.optional(),
    items: z.array(financeInvoiceLineInputSchema).min(1).max(100),
});
export const financeInvoiceResponseSchema = z.strictObject({
    data: financeInvoiceDetailSchema,
    idempotent: z.boolean(),
});
export const financeInvoiceListQuerySchema = z.strictObject({
    projectId: financeProjectReferenceIdSchema.optional(),
    status: financeInvoiceStatusSchema.optional(),
    overdue: z.boolean().optional(),
    limit: z.number().int().min(1).max(100).default(30),
    offset: z.number().int().min(0).max(1_000_000).default(0),
});
export const financeInvoiceListResponseSchema = z.strictObject({
    data: z.array(financeInvoiceSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        hasMore: z.boolean(),
    }),
    summary: z.strictObject({
        totalBilledCents: financeMoneyCentsSchema,
        totalDueCents: financeMoneyCentsSchema,
    }),
}).superRefine((response, context) => {
    if (response.page.returned !== response.data.length) {
        context.addIssue({
            code: 'custom',
            path: ['page', 'returned'],
            message: 'Finance invoice page count does not match its rows.',
        });
    }
    if (response.page.returned > 0 &&
        response.page.total < response.page.offset + response.page.returned) {
        context.addIssue({
            code: 'custom',
            path: ['page', 'total'],
            message: 'Finance invoice page extends beyond its total.',
        });
    }
    if (response.page.returned > response.page.limit) {
        context.addIssue({
            code: 'custom',
            path: ['page', 'returned'],
            message: 'Finance invoice page count exceeds its limit.',
        });
    }
    if (response.page.hasMore !== (response.page.offset + response.page.returned < response.page.total)) {
        context.addIssue({
            code: 'custom',
            path: ['page', 'hasMore'],
            message: 'Finance invoice pagination state is inconsistent.',
        });
    }
    const pageBilled = response.data.reduce((sum, invoice) => sum + BigInt(invoice.totalCents), 0n);
    const pageDue = response.data.reduce((sum, invoice) => sum + BigInt(invoice.amountDueCents), 0n);
    if (BigInt(response.summary.totalBilledCents) < pageBilled) {
        context.addIssue({
            code: 'custom',
            path: ['summary', 'totalBilledCents'],
            message: 'Finance invoice billed summary is smaller than the current page.',
        });
    }
    if (BigInt(response.summary.totalDueCents) < pageDue) {
        context.addIssue({
            code: 'custom',
            path: ['summary', 'totalDueCents'],
            message: 'Finance invoice due summary is smaller than the current page.',
        });
    }
});
//# sourceMappingURL=finance-invoice.contract.js.map